import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import {
  listAdminSubscriptions,
  manualRenewShop,
  type AdminSubscription,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import { CreditCard, Calendar, RefreshCw } from 'lucide-react';

const statusLabels: Record<string, string> = {
  trial: '试用中',
  active: '正式使用',
  readonly: '只读期',
  expired: '已过期',
};

const statusVariants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  trial: 'primary',
  active: 'success',
  readonly: 'warning',
  expired: 'danger',
};

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

export function SubscriptionsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [renewing, setRenewing] = useState<AdminSubscription | null>(null);
  const [renewDays, setRenewDays] = useState('365');
  const [renewChannel, setRenewChannel] = useState('offline_cash');
  const [renewAmount, setRenewAmount] = useState('');
  const [renewRemark, setRenewRemark] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'subscriptions', status],
    queryFn: () =>
      listAdminSubscriptions({
        status: status || undefined,
        pageSize: 50,
      }),
  });

  const renewMutation = useMutation({
    mutationFn: () =>
      manualRenewShop(renewing!.subscription.shopId, {
        days: parseInt(renewDays, 10) || 365,
        channel: renewChannel,
        amountCents: renewAmount ? Math.round(parseFloat(renewAmount) * 100) : undefined,
        remark: renewRemark.trim() || undefined,
      }),
    onSuccess: () => {
      setRenewing(null);
      setRenewDays('365');
      setRenewAmount('');
      setRenewRemark('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '续费失败'),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">套餐订阅</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          管理全部租户门店的套餐订阅状态与到期时间
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      {/* 筛选 */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="flex gap-2">
            {['', 'trial', 'active', 'readonly', 'expired'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                  status === s
                    ? 'bg-[#2563EB] text-white'
                    : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                }`}
              >
                {s === '' ? '全部' : statusLabels[s]}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 订阅列表 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            订阅列表
            <span className="text-sm font-normal text-[#64748B]">
              共 {data?.total ?? 0} 条
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.rows.length ? (
            <div className="py-12 text-center">
              <CreditCard className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">暂无订阅记录</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">门店</th>
                    <th className="pb-3 font-medium">套餐</th>
                    <th className="pb-3 font-medium">状态</th>
                    <th className="pb-3 font-medium">开始时间</th>
                    <th className="pb-3 font-medium">到期时间</th>
                    <th className="pb-3 font-medium">手动延长</th>
                    <th className="pb-3 font-medium text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.rows.map((item) => {
                    const { subscription, shop, plan } = item;
                    const days = daysUntil(subscription.endAt);
                    const isExpiringSoon = subscription.status === 'active' && days <= 30 && days > 0;
                    const isExpired = days <= 0;
                    return (
                      <tr key={subscription.id} className="text-sm">
                        <td className="py-3">
                          <div className="font-medium text-[#1E293B]">{shop?.name || '-'}</div>
                        </td>
                        <td className="py-3 text-[#64748B]">{plan?.name || '-'}</td>
                        <td className="py-3">
                          <Badge variant={statusVariants[subscription.status] || 'default'}>
                            {statusLabels[subscription.status] || subscription.status}
                          </Badge>
                        </td>
                        <td className="py-3 text-[#64748B]">
                          {new Date(subscription.startAt).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1 text-[#64748B]">
                            <Calendar className="h-4 w-4" />
                            {new Date(subscription.endAt).toLocaleDateString('zh-CN')}
                          </div>
                          {isExpiringSoon && (
                            <div className="mt-1 text-xs text-[#D97706]">还剩 {days} 天到期</div>
                          )}
                          {isExpired && subscription.status !== 'expired' && (
                            <div className="mt-1 text-xs text-[#DC2626]">已过期</div>
                          )}
                        </td>
                        <td className="py-3 text-[#64748B]">
                          {subscription.manualExtensionDays > 0
                            ? `+${subscription.manualExtensionDays} 天`
                            : '-'}
                        </td>
                        <td className="py-3">
                          <div className="flex justify-end">
                            <button
                              onClick={() => {
                                setRenewing(item);
                                setError('');
                              }}
                              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-[#2563EB] hover:bg-[#EFF6FF]"
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              手动续费
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 手动续费弹窗 */}
      <Modal
        open={!!renewing}
        onClose={() => setRenewing(null)}
        title={`手动续费 — ${renewing?.shop?.name ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setRenewing(null)} disabled={renewMutation.isPending}>
              取消
            </Button>
            <Button onClick={() => renewMutation.mutate()} disabled={renewMutation.isPending}>
              {renewMutation.isPending ? '处理中...' : '确认续费'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg bg-[#F8FAFC] p-3 text-sm text-[#64748B]">
            当前到期：<span className="font-medium text-[#1E293B]">
              {renewing && new Date(renewing.subscription.endAt).toLocaleDateString('zh-CN')}
            </span>
            <br />
            将生成一条线下收款的平台支付订单并自动确认。
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">续费天数 *</label>
              <Select value={renewDays} onChange={(e) => setRenewDays(e.target.value)}>
                <option value="30">30 天</option>
                <option value="90">90 天</option>
                <option value="180">180 天</option>
                <option value="365">365 天（一年）</option>
                <option value="730">730 天（两年）</option>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">收款方式</label>
              <Select value={renewChannel} onChange={(e) => setRenewChannel(e.target.value)}>
                <option value="offline_cash">线下现金</option>
                <option value="offline_wechat">线下微信</option>
                <option value="offline_bank">线下转账</option>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">
              实收金额（元，留空按套餐年价）
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={renewAmount}
              onChange={(e) => setRenewAmount(e.target.value)}
              placeholder="如：3999"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">备注</label>
            <Input
              value={renewRemark}
              onChange={(e) => setRenewRemark(e.target.value)}
              placeholder="可选，如：地推优惠"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
